/**
 * One chart in the workspace, with everything that belongs to it.
 *
 * ## Why this is a component and not a value in `App`
 *
 * It used to be a `chartPanel` JSX constant built from screen-level state, so
 * a layout holding two chart panes would have rendered the same constant
 * twice: two canvases, one symbol, one set of bars, one drawing tool. The
 * picker refused a second chart for exactly that reason.
 *
 * As a component it holds its own data (`useChartData`), its own drawings and
 * tool (`useDrawings`), and its own subscription to the shared socket. Two
 * panes are two instances that share nothing but the connection.
 *
 * ## What is still the screen's, and why
 *
 * **Replay.** It is a mode over the whole session, not a property of a canvas
 * — the controls, the cursor and the fine series live in `App` and are handed
 * to whichever pane is the primary one. Replaying two symbols against each
 * other independently is a different feature, not a smaller version of this.
 *
 * **The instrument master and the chart style.** Both are settings, shared by
 * every pane by definition.
 *
 * **The primary pane's data.** The header's price and staleness badge, the
 * analysis panel, the order-flow panels and replay all read the series for the
 * symbol the screen is on, so `App` owns that one and hands it in. Every other
 * chart pane owns its own through `OwnChartPane`.
 *
 * The split is in the DATA SOURCE only — both render `ChartPane`, so there is
 * one description of what a chart looks like and no second copy to drift.
 */
import type { JSX } from 'react';
import type { CrosshairTarget, Pane, Viewport } from '@aegis/core';
import type { Bar, Instrument, Trade } from '../lib/api.js';
import type { ChartStyle } from '@aegis/ui';
import type { CrosshairBus } from '../lib/crosshair.js';
import { useState } from 'react';
import { Chart } from './Chart.js';
import { ChartLoading } from './ChartLoading.js';
import { DrawingLayer } from './DrawingLayer.js';
import { DrawingToolbar } from './DrawingToolbar.js';
import { useChartData, type ChartData } from '../lib/chartData.js';
import { useDrawings, type DrawingsState } from '../lib/drawings.js';
import { useStudies } from '../lib/studies.js';
import { useReplaySeries, type ReplayContext } from '../lib/replaySeries.js';
import { IDLE_REPLAY, type ReplayState } from './ReplayControls.js';

/**
 * What the screen keeps hold of and lends to the pane that is drawing it.
 *
 * The screen owns the STATE — one cursor, one mode, one set of controls — and
 * the pane owns the SERIES. It used to own both, which quietly assumed the
 * replayed chart was showing the screen's symbol; a pane pinned to its own
 * instrument broke that assumption and lost replay altogether.
 *
 * `controls` is a function rather than an element because it needs figures only
 * the pane has: how many bars there are to step through, which bar the cursor
 * is on, and how finely that bar can be broken up.
 */
export interface ChartPaneReplay {
  readonly state: ReplayState;
  /** Withheld during replay — the annotations already know what happens next. */
  readonly hideAnalysis: boolean;
  readonly controls: (ctx: ReplayContext) => JSX.Element;
}

export interface ChartPaneProps {
  readonly pane: Pane;
  /** The symbol this pane is drawing, already resolved against the screen's. */
  readonly symbol: string;
  readonly timeframe: string;
  readonly data: ChartData;
  readonly drawings: DrawingsState;
  readonly instruments: readonly Instrument[];
  readonly style: ChartStyle;
  readonly trades: Trade[];
  readonly showAnnotations: boolean;
  /** Authoring is withheld on a phone; rendering is not. */
  readonly readOnly: boolean;
  readonly crosshair?: CrosshairBus;
  readonly crosshairTargets?: readonly CrosshairTarget[];
  /** Present only on the pane the screen's replay applies to. */
  readonly replay?: ChartPaneReplay;
}

export function ChartPane({
  pane, symbol, timeframe, data, drawings, instruments, style, trades,
  showAnnotations, readOnly, crosshair, crosshairTargets, replay,
}: ChartPaneProps): JSX.Element {
  const [viewport, setViewport] = useState<Viewport | null>(null);

  /* This pane's own series, sliced at the screen's cursor. Hooks cannot be
     conditional, so a pane with no replay runs it against an idle state — which
     returns the bars unchanged and fetches nothing. */
  const { visible, context } = useReplaySeries(
    data.bars, data.barSeconds, symbol, timeframe, replay?.state ?? IDLE_REPLAY,
  );

  const bars = replay ? visible : data.bars;
  const analysis = replay?.hideAnalysis ? null : data.analysis;

  /* Run against whatever is on screen, replay included. A study computed from
     the live series while the chart shows a replayed one would draw a line
     through bars it was not computed from — and during replay that line would
     be describing the future. */
  const studies = useStudies({
    paneId: pane.id,
    bars,
    instrument: instruments.find((i) => i.root === symbol) ?? null,
  });

  return (
    <>
      {/*
        An overlay, positioned over the canvas by CSS.

        The phone needed the opposite arrangement — React Native paints
        siblings in order, so its loader had to render AFTER the chart or be
        covered by it. Here z-index decides and the order does not, which is
        worth saying out loud because the two files look like they disagree.
      */}
      {data.bars.length === 0 && <ChartLoading symbol={symbol || null} />}
      <Chart
        symbol={symbol}
        instrument={instruments.find((i) => i.root === symbol) ?? null}
        timeframe={timeframe}
        style={style}
        bars={bars}
        analysis={analysis}
        trades={trades}
        showAnnotations={showAnnotations}
        delayedByMs={data.delayedByMs}
        onViewport={setViewport}
        paneId={pane.id}
        crosshair={crosshair}
        crosshairTargets={crosshairTargets}
        studies={studies.flatMap((st) => st.plots)}
      >
        {replay?.controls(context)}
        {/* The chart is READ-ONLY on a phone, and the toolbar is not hidden —
            it is not rendered.

            Nine tools at 28px on a 390pt screen are nine targets a thumb
            cannot separate, and the drawing they produce is unusable without a
            cursor. Hiding them with CSS would still mount the layer, bind its
            pointer handlers, and let a stray touch on the chart start a
            trendline the user cannot finish or delete.

            Drawings made on a desktop still RENDER here — reading them is the
            point. Only authoring is withheld. */}
        {!readOnly && (
          <DrawingToolbar
            tool={drawings.tool}
            onTool={drawings.setTool}
            count={drawings.drawings.length}
            limit={drawings.limit}
            selectedId={drawings.selectedId}
            onDelete={() => void drawings.remove()}
            onClear={() => void drawings.clear()}
          />
        )}
        <DrawingLayer
          viewport={viewport}
          drawings={drawings.drawings}
          tool={readOnly ? 'cursor' : drawings.tool}
          symbol={symbol}
          barSeconds={data.barSeconds}
          selectedId={drawings.selectedId}
          onSelect={drawings.select}
          onCommit={drawings.commit}
          onToolDone={() => drawings.setTool('cursor')}
        />
      </Chart>
    </>
  );
}


/**
 * A chart pane that fetches for itself.
 *
 * Every chart except the primary one. It resolves the pane's own symbol,
 * takes its own series and its own drawings, and hands both to the same view
 * the primary pane uses.
 */
export function OwnChartPane({
  pane, fallbackSymbol, fallbackTimeframe, onError, ...rest
}: Omit<ChartPaneProps, 'symbol' | 'timeframe' | 'data' | 'drawings' | 'replay'> & {
  /** Used when the pane has not chosen one of its own. */
  readonly fallbackSymbol: string;
  readonly fallbackTimeframe: string;
  readonly onError: (message: string) => void;
}): JSX.Element {
  // A pane's own symbol wins over the screen's. That is the whole point of
  // per-instance config: a chart deliberately pointed at CL keeps CL when the
  // main window moves, while one that never chose follows.
  const symbol = pane.config?.symbol ?? fallbackSymbol;
  const timeframe = pane.config?.timeframe ?? fallbackTimeframe;

  const data = useChartData({ symbol, timeframe, onError });
  const drawings = useDrawings(symbol, { onError });

  return (
    <ChartPane
      pane={pane} symbol={symbol} timeframe={timeframe}
      data={data} drawings={drawings} {...rest}
    />
  );
}
